// Carousel8.tsx - CSS Marquee with Touch Swipe Support
"use client";

import React, { useEffect, useRef, useState } from "react";

type Props = {
  children: React.ReactNode;
  options?: any;
  speed?: number;
  slideClassName?: string;
  allowManualSwipe?: boolean;
};

export default function Carousel8({
  children,
  speed = 2,
  slideClassName = "",
  allowManualSwipe = false,
}: Props) {
  const containerRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);
  const [isPaused, setIsPaused] = useState(false);

  const childArray = React.Children.toArray(children).filter(Boolean);

  // Calculate animation duration based on speed
  const duration = Math.max(20 / speed, 10);

  // Duplicate children for seamless loop
  const duplicatedChildren = [...childArray, ...childArray, ...childArray];

  // Touch/Mouse handlers for manual swipe
  const handleStart = (clientX: number) => {
    if (!allowManualSwipe || !containerRef.current) return;
    setIsDragging(true);
    setIsPaused(true);
    setStartX(clientX - containerRef.current.offsetLeft);
    setScrollLeft(containerRef.current.scrollLeft);
  };

  const handleMove = (clientX: number) => {
    if (!isDragging || !containerRef.current) return;
    const x = clientX - containerRef.current.offsetLeft;
    const walk = (x - startX) * 2;
    containerRef.current.scrollLeft = scrollLeft - walk;
  };

  const handleEnd = () => {
    setIsDragging(false);
    // Resume autoscroll after 1 second of no interaction
    if (allowManualSwipe) {
      setTimeout(() => {
        setIsPaused(false);
      }, 1000);
    }
  };

  // Mouse events
  const onMouseDown = (e: React.MouseEvent) => handleStart(e.pageX);
  const onMouseMove = (e: React.MouseEvent) => handleMove(e.pageX);
  const onMouseUp = () => handleEnd();
  const onMouseLeave = () => {
    if (isDragging) handleEnd();
  };

  // Touch events
  const onTouchStart = (e: React.TouchEvent) =>
    handleStart(e.touches[0].clientX);
  const onTouchMove = (e: React.TouchEvent) => handleMove(e.touches[0].clientX);
  const onTouchEnd = () => handleEnd();

  // Reset scroll position for infinite loop effect when using manual scroll
  useEffect(() => {
    if (!containerRef.current || !isPaused) return;

    const container = containerRef.current;
    const handleScroll = () => {
      const maxScroll = container.scrollWidth - container.clientWidth;
      const oneThird = maxScroll / 3;

      if (container.scrollLeft >= oneThird * 2) {
        container.scrollLeft = oneThird;
      } else if (container.scrollLeft <= 0) {
        container.scrollLeft = oneThird;
      }
    };

    container.addEventListener("scroll", handleScroll);
    return () => container.removeEventListener("scroll", handleScroll);
  }, [isPaused]);

  return (
    <div
      ref={containerRef}
      className={`overflow-hidden w-full ${
        allowManualSwipe ? "cursor-grab active:cursor-grabbing" : ""
      } ${isPaused ? "overflow-x-auto scrollbar-hide" : ""}`}
      onMouseDown={allowManualSwipe ? onMouseDown : undefined}
      onMouseMove={allowManualSwipe ? onMouseMove : undefined}
      onMouseUp={allowManualSwipe ? onMouseUp : undefined}
      onMouseLeave={allowManualSwipe ? onMouseLeave : undefined}
      onTouchStart={allowManualSwipe ? onTouchStart : undefined}
      onTouchMove={allowManualSwipe ? onTouchMove : undefined}
      onTouchEnd={allowManualSwipe ? onTouchEnd : undefined}
      style={{ WebkitOverflowScrolling: "touch" }}
    >
      <div
        ref={contentRef}
        className={`flex gap-3 md:gap-6 pl-3 md:pl-6 ${
          !isPaused ? "animate-marquee" : ""
        }`}
        style={{
          animationDuration: `${duration}s`,
          width: "max-content",
        }}
      >
        {duplicatedChildren.map((child, i) => (
          <div key={i} className={`flex-shrink-0 ${slideClassName}`}>
            {child}
          </div>
        ))}
      </div>

      <style jsx>{`
        @keyframes marquee {
          0% {
            transform: translateX(0);
          }
          100% {
            transform: translateX(-33.33%);
          }
        }
        .animate-marquee {
          animation: marquee linear infinite;
        }
        .scrollbar-hide {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
      `}</style>
    </div>
  );
}
